import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import type { Job } from '@/api/types';
import { isTerminalJobStatus } from '@/utils/jobStatus';

const ACTIVE_POLL_MS = 2000;
const MAX_BACKOFF_MS = 30000;

type Options = {
  onTerminal?: (job: Job) => void;
};

export function useJobPolling(jobId: string | null | undefined, options?: Options) {
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const onTerminalRef = useRef(options?.onTerminal);
  onTerminalRef.current = options?.onTerminal;

  const refresh = useCallback(async () => {
    if (!jobId) return null;
    const data = await api.jobs.get(jobId);
    setJob(data);
    setError(null);
    return data;
  }, [jobId]);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let backoffMs = ACTIVE_POLL_MS;
    const activeJobId = jobId;

    async function poll() {
      if (cancelled) return;
      try {
        const data = await api.jobs.get(activeJobId);
        if (cancelled) return;
        setJob(data);
        setError(null);
        backoffMs = ACTIVE_POLL_MS;
        if (isTerminalJobStatus(data.status)) {
          onTerminalRef.current?.(data);
          return;
        }
        timeoutId = setTimeout(poll, ACTIVE_POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load job');
        backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        timeoutId = setTimeout(poll, backoffMs);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [jobId]);

  return { job, error, loading, refresh };
}
